import React, { useEffect, useState } from 'react';
import 'styles/main.css';

import moment from 'moment-timezone';
import update from 'immutability-helper';

import Alert from 'views/Alert.js';
import AlertStack from 'views/AlertStack.js';
import { hasConsentDecision, initTracking } from 'files/Analytics.js';
import API from 'files/api.js';
import Appearance from 'styles/Appearance.js';
import BookingLayer from 'structure/BookingLayer.js';
import BookingSuccess from 'views/BookingSuccess.js';
import ConsentBanner from 'views/ConsentBanner.js';
import ContactForm from 'views/ContactForm.js';
import FamilyBanner from 'views/FamilyBanner.js';
import Features from 'views/Features.js';
import Footer from 'views/Footer.js';
import Header from 'views/Header.js';
import Hero from 'views/Hero.js';
import Stats from 'views/Stats.js';
import { VelocityComponent } from 'velocity-react';

window.theme = 'light';

const App = () => {

    const [alerts, setAlerts] = useState([]);
    const [bookingState, setBookingState] = useState({ visible: false, initialZip: '' });
    const [consentVisible, setConsentVisible] = useState(false);
    const [content, setContent] = useState({ opacity: 0, top: -50 });
    const [successState, setSuccessState] = useState({ visible: false });
    const [theme, setTheme] = useState(window.theme);

    const utils = {
        api: {
            headers: () => {
                return {
                    'Content-Type': 'application/json',
                    'X-API': `Version ${API.version}`,
                    'X-Timezone': `TZ ${moment.tz.guess()}`
                }
            }
        },
        alert: {
            show: props => {
                try {
                    setAlerts(alerts => {
                        return update(alerts, {
                            $push: [{
                                id: `${moment().unix()}-${Math.random()}`,
                                utils: utils,
                                ...props
                            }]
                        });
                    });
                } catch(e) {
                    console.error('Alert show error:', e);
                }
            },
            showAsync: async props => {
                return new Promise(async (resolve, reject) => {
                    try {
                        setAlerts(alerts => {
                            return update(alerts, {
                                $push: [{
                                    id: `${moment().unix()}-${Math.random()}`,
                                    utils: utils,
                                    onClose: resolve,
                                    ...props
                                }]
                            });
                        });
                    } catch(e) {
                        reject(e.message);
                    }
                });
            }
        },
        booking: {
            show: zip => setBookingState({ visible: true, initialZip: zip, mode: 'booking' }),
            showCallback: () => setBookingState({ visible: true, mode: 'callback' }),
            showCallnow: () => setBookingState({ visible: true, mode: 'call_now' }),
            hide: () => setBookingState(state => ({ ...state, visible: false })),
            confirmed: props => {
                setBookingState(state => ({ ...state, visible: false }));
                setSuccessState({ visible: true, ...props });
            }
        }
    }

    const onSetStyleSheetProperties = () => {
        document.body.className = window.theme;
        document.documentElement.style.setProperty('--theme', window.theme);
        document.documentElement.style.setProperty('--text', Appearance.colors.text());
        document.documentElement.style.setProperty('--textfield', Appearance.colors.textField());
        document.documentElement.style.setProperty('--soft_border', Appearance.colors.softBorder());
        let themeMeta = document.querySelector('meta[name="theme-color"]');
        if(themeMeta) {
            themeMeta.setAttribute('content', Appearance.colors.background());
        }
        setTheme(window.theme);
    }

    const onUpdateTheme = evt => {
        window.theme = evt.matches ? 'dark' : 'light';
        onSetStyleSheetProperties();
    }

    useEffect(() => {

        // theme detection and listener
        window.theme = 'light';
        onSetStyleSheetProperties();
        let mq = window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)');
        if(mq && mq.addEventListener) {
            mq.addEventListener('change', onUpdateTheme);
        }

        // animate content into view
        setContent({ opacity: 1, top: 0 });

        // load tracking (GTM + Firebase) with consent-mode defaults, and show
        // the cookie banner until the visitor has made a choice
        initTracking();
        setConsentVisible(!hasConsentDecision());

        return () => {
            if(mq && mq.removeEventListener) {
                mq.removeEventListener('change', onUpdateTheme);
            }
        }
    }, []);

    return (
        <div
        className={`root-container ${theme}`}
        style={{
            backgroundColor: '#FFFFFF',
            minHeight: '100vh',
            position: 'relative',
            width: '100%'
        }}>
            <Header utils={utils} />
            <VelocityComponent
            animation={{
                opacity: content.opacity,
                top: content.top
            }}
            duration={750}
            easing={[250, 20]}>
                <main style={{ position: 'relative', width: '100%' }}>
                    <Hero utils={utils} />
                    <Stats utils={utils} />
                    <Features utils={utils} />
                    <ContactForm utils={utils} />
                    <FamilyBanner utils={utils} />
                </main>
            </VelocityComponent>
            <Footer
            utils={utils}
            onManageCookies={() => setConsentVisible(true)} />

            {consentVisible && (
                <ConsentBanner onResolved={() => setConsentVisible(false)} />
            )}

            {bookingState.visible && (
                <BookingLayer
                initialZip={bookingState.initialZip}
                mode={bookingState.mode || 'booking'}
                utils={utils} />
            )}

            {successState.visible && (
                <BookingSuccess
                demoRequestID={successState.demo_request_id}
                onClose={() => setSuccessState({ visible: false})}
                utils={utils} />
            )}

            <AlertStack>
                {alerts.map((alert, index) => (
                    <Alert
                    {...alert}
                    key={alert.id}
                    utils={utils}
                    index={(alerts.length - 1) - index}
                    onClose={id => {
                        if(typeof(alert.onClose) === 'function') {
                            alert.onClose();
                        }
                        setAlerts(alerts => {
                            return alerts.filter(a => id !== a.id);
                        });
                    }} />
                ))}
            </AlertStack>
        </div>
    )
}

export default App;
